import { theme } from '@focaldata/cin-ui-components'
import { Alert, AlertTitle } from '@mui/material'
import { useEffect, useState } from 'react'
import { useAnalyticsFeatureFlag } from '../../../../contexts/AnalyticsFeatureFlagContext'
import CompleteChartList from '../../../../controls/CompleteChartList'
import { useCompleteChartListData } from '../../../../controls/CompleteChartList/CompleteChartList.hooks'
import LanguageSelect from '../../../../controls/LanguageSelect'
import QuotaCards from '../../../../controls/QuotaCards'
import { SurveyState } from '../../../../data/gql-gen/dashboard/graphql'
import { SelectedFilter } from '../../../../data/model/results'
import { useFieldworkAudience } from '../../../../hooks/audience/useFieldworkAudience'
import useGetSurveyQuotas from '../../../../hooks/useGetSurveyQuotas'
import useGetSurveyState from '../../../../hooks/useGetSurveyState'
import { useResultsLanguages } from '../../../../hooks/useResultsLanguages'
import { useSurveyId } from '../../../../hooks/useSurveyId'
import ResultsPageLayout from '../../../../layouts/ResultsPageLayout'
import { AnalyticsResultsFilters } from './AnalyticsResultsFilters'
import { TablePagination } from './DefaultMuiTablePagination'
import { ResultsFilters } from './ResultsFilters'
import { ResultsOptions } from './ResultsOptions'

export const ITEMS_PER_PAGE = 50

// `multiMarketV2` marks the per-market view of a multi-market project: the
// options bar stays but adapts (no caption/duplicate, market-level pause/end,
// master-questionnaire export) — see ResultsOptions.
const Results = ({ multiMarketV2 = false }: { multiMarketV2?: boolean }) => {
  const surveyId = useSurveyId()
  const { isLoading: isCategoricalVersionLoading, useAnalyticsAPI } =
    useAnalyticsFeatureFlag()
  const [showQuotas, setShowQuotas] = useState<boolean>()
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([])
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE)
  const [page, setPage] = useState(0)
  const [pickedLanguage, setPickedLanguage] = useState<string>()

  // fd-core-responses resolves question text per field, and standard audience
  // questions come from an English-canonical library — so the language has to be
  // sent explicitly or a non-English survey shows English audience labels. The
  // codes come from the survey's own fieldwork, the same state the audience
  // builder's language switch reads.
  const {
    primaryLanguage,
    availableLanguages,
    loading: languageLoading
  } = useResultsLanguages([surveyId])
  // Falls back to the survey's primary language whenever nothing is picked, so
  // single-language surveys are localised with no UI at all.
  const language =
    pickedLanguage && availableLanguages.includes(pickedLanguage)
      ? pickedLanguage
      : primaryLanguage

  // Every consumer of `language` is held until the lookup settles, so the page
  // makes one localised request rather than an English one it then replaces.
  const { chartData, totalCount, summaryDataLoading, rehydrationStatus } =
    useCompleteChartListData(
      surveyId,
      selectedFilters,
      page,
      rowsPerPage,
      language,
      languageLoading
    )

  const { fieldworkAudience } = useFieldworkAudience()

  const areQuotasApplied = !!fieldworkAudience?.quotas.length
  const {
    getSurveyQuotas,
    quotaResults,
    loading: quotaResultsLoading
  } = useGetSurveyQuotas({
    surveyId,
    language
  })
  const quotaResultsAndType = quotaResults?.map((quotaResult) => {
    return {
      ...quotaResult,
      type: chartData.find((sdEntity) => sdEntity.id === quotaResult.id)
        ?.questionType
    }
  })
  const { surveyState = SurveyState.Live } = useGetSurveyState()

  const disableShowQuotas = !areQuotasApplied

  // Fetches on first reveal and again whenever the language changes while the
  // cards are open — quota cards carry question text too, so they have to be
  // re-resolved alongside the charts. Held until the lookup settles so revealing
  // the cards can't fire an unlocalised request that a localised one replaces.
  useEffect(() => {
    if (showQuotas && !languageLoading) {
      getSurveyQuotas(surveyId)
    }
  }, [showQuotas, language, languageLoading, surveyId, getSurveyQuotas])

  const handleUpdateFilters = (filters: SelectedFilter[]) => {
    setSelectedFilters(filters)
    // Keep user on same page - questions don't change, only respondent counts do
  }

  if (isCategoricalVersionLoading) {
    return <ResultsPageLayout isLoading resultCards={<></>} />
  }

  // Always show pagination control - use -1 for unknown total count
  // MUI TablePagination handles -1 by showing "X-Y" without total
  const displayCount = totalCount === -1 ? -1 : totalCount

  const paginationControl = (
    <TablePagination
      count={displayCount}
      labelRowsPerPage="Charts per page"
      page={page}
      onPageChange={(e, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(Number(e.target.value))
        setPage(0) // Reset to first page when changing items per page
      }}
      showFirstButton
      showLastButton={totalCount !== -1} // Hide last button when total is unknown
    />
  )

  return (
    <ResultsPageLayout
      isLoading={summaryDataLoading}
      optionsBar={
        <>
          <ResultsOptions
            disableShowQuotas={disableShowQuotas}
            surveyState={surveyState}
            onShowQuotasChange={setShowQuotas}
            selectedFilters={selectedFilters}
            language={language}
            multiMarketV2={multiMarketV2}
          />
          <LanguageSelect
            languages={availableLanguages}
            selectedLanguage={language}
            onLanguageChange={setPickedLanguage}
            ariaLabel="Results language"
            padding={theme.spacing(1, 0)}
          />
        </>
      }
      resultCards={
        <>
          {rehydrationStatus === 'rehydrating' && (
            <Alert severity="info" sx={{ marginBottom: 2 }}>
              <AlertTitle>Preparing your survey data</AlertTitle>
              This can take up to 15 minutes for large surveys. The page will
              refresh automatically when the data is ready — no need to reload.
            </Alert>
          )}
          {showQuotas && (
            <QuotaCards
              quotaResults={quotaResultsAndType}
              quotaResultsLoading={quotaResultsLoading}
              areQuotasApplied={areQuotasApplied}
            />
          )}
          {paginationControl}
          <CompleteChartList
            chartData={chartData}
            isLoading={summaryDataLoading}
            showAudienceCriteriaQuestions
          />
          {paginationControl}
        </>
      }
      filters={
        <>
          {useAnalyticsAPI ? (
            <AnalyticsResultsFilters
              selectedFilters={selectedFilters}
              setSelectedFilters={handleUpdateFilters}
              language={language}
              languageLoading={languageLoading}
            />
          ) : (
            <ResultsFilters
              selectedFilters={selectedFilters}
              setSelectedFilters={handleUpdateFilters}
            />
          )}
        </>
      }
    />
  )
}

export default Results
